import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { useParams } from 'react-router-dom';

import { UseChartResponse, XAxisOption } from './types';
import { getTicketsSold, processTimeline, reducer, snapBasketEnd, snapBasketStart, TICKETS_SOLD } from './utils';
import { useHighlights } from 'features/common/hooks/useHighlights';

/** Pick the smallest granularity that keeps the full sales span readable. */
const getOptimalAxis = (sales: { date: Date }[]): XAxisOption => {
	if (sales.length < 2) return 'days';
	const spanDays = (sales[sales.length - 1].date.valueOf() - sales[0].date.valueOf()) / 86400000;
	if (spanDays >= 56) return 'months';
	if (spanDays >= 15) return 'weeks';
	return 'days';
};

const useChart = (chart: string): UseChartResponse => {
	const { id }: { id: string } = useParams();
	const [displayWindow, setDisplay] = useReducer(reducer, {
		xAxis: 'days',
		windowStart: 0,
		windowSize: 0,
		windowSizeShadow: 0,
		dataPoints: 0,
	});
	const hasInitializedRef = useRef(false);
	/* Tracks the last xAxis value for which we reset the window.
	 * Prevents the granularity-reset branch from firing on every eventHighlights
	 * reference change — it fires only when xAxis genuinely changes. */
	const prevXAxisRef = useRef<XAxisOption | null>(null);

	const { eventHighlights, isLoading } = useHighlights(id, true);

	const dataByTimeline = useMemo(
		() => processTimeline({ eventHighlights, timeline: displayWindow.xAxis }),
		[eventHighlights, displayWindow.xAxis]
	);

	// Memoized so the windowed slice keeps a stable reference between renders when
	// nothing relevant changed — feeds the chart `data` prop, so a fresh array each
	// render would defeat the memo() on the chart primitives.
	const displayedWindow = useMemo(
		() =>
			dataByTimeline
				? dataByTimeline.sales.slice(displayWindow.windowStart, displayWindow.windowStart + displayWindow.windowSize)
				: [],
		[dataByTimeline, displayWindow.windowStart, displayWindow.windowSize]
	);

	useEffect(() => {
		if (!eventHighlights?.sales?.length) return;

		if (!hasInitializedRef.current) {
			// First load: pick optimal axis and show all data atomically.
			hasInitializedRef.current = true;
			const optAxis = getOptimalAxis(eventHighlights.sales as { date: Date }[]);
			prevXAxisRef.current = optAxis;
			const processed = processTimeline({ eventHighlights, timeline: optAxis });
			if (processed?.sales.length) {
				setDisplay({ type: 'smart_init', xAxis: optAxis, payload: processed.sales.length });
			}
		} else if (displayWindow.xAxis !== prevXAxisRef.current) {
			// User changed timeline granularity: reset window to show all data in the new granularity.
			prevXAxisRef.current = displayWindow.xAxis;
			if (dataByTimeline?.sales.length) {
				setDisplay({ type: 'init', payload: dataByTimeline.sales.length });
			}
		}
	}, [eventHighlights, displayWindow.xAxis]);

	// Date range metadata derived from the full (unwindowed) sales data.
	// Expanded to basket boundaries so the first/last full basket is always reachable in the calendar.
	const rawSales = eventHighlights?.sales ?? [];
	const rawMinDate = rawSales.length > 0 ? rawSales[0].date : undefined;
	const rawMaxDate = rawSales.length > 0 ? rawSales[rawSales.length - 1].date : undefined;
	const minDate = rawMinDate ? snapBasketStart(rawMinDate, displayWindow.xAxis) : undefined;
	const maxDate = rawMaxDate ? snapBasketEnd(rawMaxDate, displayWindow.xAxis) : undefined;

	const timelineSales = dataByTimeline?.sales ?? [];
	// windowStartDate is the raw basket-start of the first window entry — the calendar
	// start picker shows "first day of range" which is already the basket start.
	const windowStartDate = timelineSales[displayWindow.windowStart]?.date;
	const lastBasketDate =
		timelineSales[Math.min(displayWindow.windowStart + displayWindow.windowSize - 1, timelineSales.length - 1)]?.date;
	// Each timeline entry is a basket-start date; snapBasketEnd converts it to the
	// last moment of that basket so the calendar pre-selects the correct end day.
	const windowEndDate = lastBasketDate ? snapBasketEnd(lastBasketDate, displayWindow.xAxis) : undefined;

	// Converts a chosen date range to window indices within the current granularity.
	// Snaps start/end outward to basket boundaries so clicking any day in a basket selects the full basket.
	const setDateRange = useCallback(
		(start: Date, end: Date) => {
			const sales = dataByTimeline?.sales;
			if (!sales?.length) return;
			const snappedStart = snapBasketStart(start, displayWindow.xAxis);
			const snappedEnd = snapBasketEnd(end, displayWindow.xAxis);
			const startIdx = sales.findIndex((d) => d.date.getTime() >= snappedStart.getTime());
			const endIdx = sales.reduce<number>((last, d, i) => (d.date.getTime() <= snappedEnd.getTime() ? i : last), -1);
			if (startIdx < 0 || endIdx < startIdx) return;
			setDisplay({ type: 'set_window', start: startIdx, size: endIdx - startIdx + 1 });
		},
		[dataByTimeline, displayWindow.xAxis]
	);

	const dateRangeInfo = { minDate, maxDate, windowStartDate, windowEndDate, setDateRange };

	// Memoized so the derived series arrays keep stable references — same rationale
	// as displayedWindow above (defeating the chart primitives' memo() otherwise).
	const ticketsSoldData = useMemo(
		() => getTicketsSold(displayedWindow, eventHighlights?.ticket_types_inventory),
		[displayedWindow, eventHighlights?.ticket_types_inventory]
	);

	if (chart === TICKETS_SOLD) {
		return {
			...ticketsSoldData,
			setDisplay,
			displayWindow,
			isLoading,
			...dateRangeInfo,
		};
	} else {
		return { dataFrame: displayedWindow, setDisplay, displayWindow, isLoading, ...dateRangeInfo };
	}
};

export { useChart };
